import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity, Layers, Server, ShieldCheck, FileText, Bell,
  AlertCircle, Search, Lightbulb, CheckCircle2, CheckCheck,
  RotateCcw, Megaphone, Cog, ChevronLeft, ChevronRight, ArrowRight,
  Send, Mail, MessageSquare
} from "lucide-react";
import { openConsultationModal } from "./ConsultationModal";

const TABS = [
  { id: "realtime", label: "Live Realtime (1s)", icon: Activity, image: "/brand/guardian/dashboard-realtime.png" },
  { id: "applications", label: "Applications", icon: Layers, image: "/brand/guardian/dashboard-infrastructure.png" },
  { id: "infrastructure", label: "Infrastructure", icon: Server, image: "/brand/guardian/dashboard-infrastructure.png" },
  { id: "security", label: "Security", icon: ShieldCheck, image: "/brand/guardian/dashboard-security.jpg" },
  { id: "logs", label: "System Logs", icon: FileText, image: "/brand/guardian/dashboard-logs.jpg" },
  { id: "alerts", label: "Alerts", icon: Bell, image: "/brand/guardian/dashboard-alerts.jpg" }
];

const WORKFLOW_STEPS = [
  {
    title: "Detect",
    desc: "Real-time monitoring across your stack",
    icon: Search
  },
  {
    title: "Explain",
    desc: "See exactly why an alert was triggered",
    icon: FileText
  },
  {
    title: "Respond",
    desc: "Automated & one-click remediation",
    icon: Cog
  },
  {
    title: "Contain",
    desc: "Block threats and quarantine risks",
    icon: ShieldCheck
  },
  {
    title: "Recover",
    desc: "Restart services, clear cache, and restore health",
    icon: RotateCcw
  },
  {
    title: "Notify",
    desc: "Alerts via Slack, Email, Telegram, Webhooks",
    icon: Megaphone
  }
];

export const GuardianRealtimeSlider = () => {
  // Start on 'alerts' tab matching the exact reference screenshot
  const [activeTabIdx, setActiveTabIdx] = useState(5);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-cycle through tabs every 7 seconds if not paused
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setActiveTabIdx((prev) => (prev + 1) % TABS.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [isPaused]);

  const activeTab = TABS[activeTabIdx];

  const handlePrev = (e) => {
    e?.stopPropagation();
    setActiveTabIdx((prev) => (prev - 1 + TABS.length) % TABS.length);
  };

  const handleNext = (e) => {
    e?.stopPropagation();
    setActiveTabIdx((prev) => (prev + 1) % TABS.length);
  };

  return (
    <div
      className="w-full max-w-[1400px] mx-auto select-none"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* ── 1. TOP FLOATING PILL TABS BAR ───────────────────────────────────── */}
      <div className="flex items-center justify-start sm:justify-center gap-1.5 sm:gap-2.5 overflow-x-auto pb-4 mb-6 sm:mb-8 no-scrollbar">
        {TABS.map((tab, idx) => {
          const isActive = activeTabIdx === idx;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTabIdx(idx)}
              className={`inline-flex items-center gap-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-[13px] font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                isActive
                  ? "bg-[#EDE9FE] text-[#5B4DF5] font-semibold border border-[#DDD6FE] shadow-xs"
                  : "bg-white text-neutral-600 hover:text-neutral-900 border border-neutral-200/80 hover:border-neutral-300 shadow-2xs hover:bg-neutral-50/80"
              }`}
            >
              <Icon className={`w-3.5 sm:w-4 h-3.5 sm:h-4 ${isActive ? "text-[#5B4DF5]" : "text-neutral-500"}`} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── 2. MAIN COMPOSITION: LEFT CARDS + CENTER WINDOW + RIGHT CARDS ──── */}
      <div className="relative flex flex-col xl:flex-row items-center justify-center gap-6 xl:gap-8 my-4">
        
        {/* ── LEFT COLUMN CARDS (Desktop: Flanking Left) ────────────────────── */}
        <div className="w-full xl:w-[260px] flex flex-col sm:flex-row xl:flex-col gap-4 shrink-0 order-2 xl:order-1">
          
          {/* Card 1: Threat Detected */}
          <div className="flex-1 p-4 sm:p-4.5 rounded-2xl bg-white border border-red-100 shadow-sm text-left relative group hover:shadow-md transition-all">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
                <AlertCircle className="w-4 h-4 text-red-500" />
              </div>
              <h4 className="text-xs sm:text-sm font-bold text-red-600">
                Threat Detected
              </h4>
            </div>
            <p className="text-[11px] text-neutral-500 leading-snug">
              Brute force login attempts identified in real time.
            </p>
            {/* Mini Red Wave Sparkline */}
            <div className="mt-3 pt-2 border-t border-red-50 flex items-center justify-between">
              <svg viewBox="0 0 100 22" className="w-24 h-4 overflow-visible">
                <path
                  d="M 0,11 Q 12,2 25,11 T 50,11 T 75,4 T 100,11"
                  fill="none"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span className="text-[10px] font-mono font-bold text-red-500">Alert #1</span>
            </div>
            {/* Desktop Connector Dot */}
            <div className="hidden xl:block absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 border-red-500 shadow-xs z-20">
              <span className="absolute inset-0.5 rounded-full bg-red-500 animate-ping opacity-75" />
            </div>
          </div>

          {/* Card 2: Analyzing */}
          <div className="flex-1 p-4 sm:p-4.5 rounded-2xl bg-white border border-purple-100 shadow-sm text-left relative group hover:shadow-md transition-all">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center shrink-0">
                <Search className="w-4 h-4 text-purple-600" />
              </div>
              <h4 className="text-xs sm:text-sm font-bold text-purple-600">
                Analyzing
              </h4>
            </div>
            <p className="text-[11px] text-neutral-500 leading-snug">
              Guardian inspects logs, correlates events and finds the root cause.
            </p>
            {/* Desktop Connector Dot */}
            <div className="hidden xl:block absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 border-purple-500 shadow-xs z-20">
              <span className="absolute inset-0.5 rounded-full bg-purple-500 animate-ping opacity-75" />
            </div>
          </div>

          {/* Card 3: Action Suggested */}
          <div className="flex-1 p-4 sm:p-4.5 rounded-2xl bg-white border border-amber-100 shadow-sm text-left relative group hover:shadow-md transition-all">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
                <Lightbulb className="w-4 h-4 text-amber-500" />
              </div>
              <h4 className="text-xs sm:text-sm font-bold text-amber-600">
                Action Suggested
              </h4>
            </div>
            <p className="text-[11px] text-neutral-500 leading-snug">
              Shows exact fix with commands and one-click actions.
            </p>
            {/* Desktop Connector Dot */}
            <div className="hidden xl:block absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 border-amber-500 shadow-xs z-20">
              <span className="absolute inset-0.5 rounded-full bg-amber-500 animate-ping opacity-75" />
            </div>
          </div>

        </div>

        {/* ── CENTER COLUMN: MACOS APPLICATION WINDOW FRAME ─────────────────── */}
        <div className="flex-1 w-full max-w-[820px] rounded-3xl bg-white border border-neutral-200/90 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08)] overflow-hidden text-left relative order-1 xl:order-2 group">
          
          {/* Subtle Ambient Behind Window */}
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[220px] rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />

          {/* Window Chrome Title Bar */}
          <div className="px-4 sm:px-6 py-3.5 bg-neutral-50/80 border-b border-neutral-200/80 flex items-center justify-between gap-4 relative z-10">
            {/* Traffic Lights + Breadcrumb */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-[#FF5F56]" />
                <span className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
                <span className="w-3 h-3 rounded-full bg-[#27C93F]" />
              </div>
              <div className="flex items-center gap-2 pl-3 border-l border-neutral-200 text-xs font-mono text-neutral-500">
                <span>Guardian</span>
                <span>›</span>
                <span>Fleet</span>
                <span>›</span>
                <span className="font-bold text-neutral-800">srv807435</span>
              </div>
            </div>

            {/* Live Indicator Pill */}
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-600 text-[11px] font-mono font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>Live (10s)</span>
            </div>
          </div>

          {/* Main Slide Image */}
          <div className="relative aspect-[16/10.2] sm:aspect-[16/10] w-full bg-[#FAFAFC] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab.id}
                initial={{ opacity: 0, scale: 0.99 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.995 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                className="w-full h-full flex items-center justify-center p-1 sm:p-2"
              >
                <img
                  src={activeTab.image}
                  alt={`Guardian ${activeTab.label} Dashboard`}
                  className="w-full h-full object-contain rounded-xl shadow-xs border border-neutral-100"
                  loading="eager"
                />
              </motion.div>
            </AnimatePresence>

            {/* Left / Right Carousel Arrow Buttons */}
            <button
              onClick={handlePrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-neutral-700 hover:text-neutral-900 border border-neutral-200 shadow-md backdrop-blur-md flex items-center justify-center transition-all opacity-80 group-hover:opacity-100 hover:scale-105 active:scale-95 cursor-pointer z-20"
              aria-label="Previous Dashboard View"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <button
              onClick={handleNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-neutral-700 hover:text-neutral-900 border border-neutral-200 shadow-md backdrop-blur-md flex items-center justify-center transition-all opacity-80 group-hover:opacity-100 hover:scale-105 active:scale-95 cursor-pointer z-20"
              aria-label="Next Dashboard View"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Bottom Dot Indicators */}
          <div className="flex items-center justify-center gap-1.5 py-3 bg-white border-t border-neutral-100">
            {TABS.map((t, idx) => (
              <button
                key={t.id}
                onClick={() => setActiveTabIdx(idx)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  activeTabIdx === idx ? "w-6 bg-[#5B4DF5]" : "w-1.5 bg-neutral-200 hover:bg-neutral-300"
                }`}
                aria-label={`Slide to ${t.label}`}
              />
            ))}
          </div>

        </div>

        {/* ── RIGHT COLUMN CARDS (Desktop: Flanking Right) ──────────────────── */}
        <div className="w-full xl:w-[260px] flex flex-col sm:flex-row xl:flex-col gap-4 shrink-0 order-3">
          
          {/* Card 4: Auto Contained */}
          <div className="flex-1 p-4 sm:p-4.5 rounded-2xl bg-white border border-emerald-100 shadow-sm text-left relative group hover:shadow-md transition-all">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              </div>
              <h4 className="text-xs sm:text-sm font-bold text-emerald-600">
                Auto Contained
              </h4>
            </div>
            <p className="text-[11px] text-neutral-500 leading-snug">
              Suspicious IP blocked automatically.
            </p>
            {/* Mini Green Wave Sparkline */}
            <div className="mt-3 pt-2 border-t border-emerald-50 flex items-center justify-between">
              <svg viewBox="0 0 100 22" className="w-24 h-4 overflow-visible">
                <path
                  d="M 0,11 Q 25,18 50,4 T 100,11"
                  fill="none"
                  stroke="#10B981"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span className="text-[10px] font-mono font-bold text-emerald-500">Blocked</span>
            </div>
            {/* Desktop Connector Dot */}
            <div className="hidden xl:block absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 border-emerald-500 shadow-xs z-20">
              <span className="absolute inset-0.5 rounded-full bg-emerald-500 animate-ping opacity-75" />
            </div>
          </div>

          {/* Card 5: Notified */}
          <div className="flex-1 p-4 sm:p-4.5 rounded-2xl bg-white border border-blue-100 shadow-sm text-left relative group hover:shadow-md transition-all">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0">
                <Bell className="w-4 h-4 text-blue-600" />
              </div>
              <h4 className="text-xs sm:text-sm font-bold text-blue-600">
                Notified
              </h4>
            </div>
            <p className="text-[11px] text-neutral-500 leading-snug mb-3">
              Instant alerts to Slack, Email, Telegram and Webhooks.
            </p>
            {/* Notification App Icons */}
            <div className="flex items-center gap-2 pt-1 border-t border-blue-50">
              <span className="w-6 h-6 rounded-lg bg-[#4A154B]/10 flex items-center justify-center text-[#4A154B] text-[10px] font-bold">#</span>
              <span className="w-6 h-6 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-600">
                <Mail className="w-3 h-3" />
              </span>
              <span className="w-6 h-6 rounded-lg bg-sky-500/10 flex items-center justify-center text-sky-500">
                <Send className="w-3 h-3" />
              </span>
              <span className="w-6 h-6 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-600">
                <MessageSquare className="w-3 h-3" />
              </span>
            </div>
            {/* Desktop Connector Dot */}
            <div className="hidden xl:block absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 border-blue-500 shadow-xs z-20">
              <span className="absolute inset-0.5 rounded-full bg-blue-500 animate-ping opacity-75" />
            </div>
          </div>

          {/* Card 6: Resolved */}
          <div className="flex-1 p-4 sm:p-4.5 rounded-2xl bg-white border border-indigo-100 shadow-sm text-left relative group hover:shadow-md transition-all">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-full bg-indigo-500/10 flex items-center justify-center shrink-0">
                <CheckCheck className="w-4 h-4 text-indigo-600" />
              </div>
              <h4 className="text-xs sm:text-sm font-bold text-indigo-600">
                Resolved
              </h4>
            </div>
            <p className="text-[11px] text-neutral-500 leading-snug">
              Issue fixed and monitored to prevent recurrence.
            </p>
            {/* Mini Violet Wave Sparkline */}
            <div className="mt-3 pt-2 border-t border-indigo-50 flex items-center justify-between">
              <svg viewBox="0 0 100 22" className="w-24 h-4 overflow-visible">
                <path
                  d="M 0,11 Q 25,4 50,18 T 100,11"
                  fill="none"
                  stroke="#6366F1"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span className="text-[10px] font-mono font-bold text-indigo-500">Healthy</span>
            </div>
            {/* Desktop Connector Dot */}
            <div className="hidden xl:block absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 border-indigo-500 shadow-xs z-20">
              <span className="absolute inset-0.5 rounded-full bg-indigo-500 animate-ping opacity-75" />
            </div>
          </div>

        </div>

      </div>

      {/* ── 3. BOTTOM 6-PILLAR HORIZONTAL WORKFLOW STEPPER STRIP ───────────── */}
      <div className="mt-10 sm:mt-12 p-3 sm:p-4 rounded-2xl sm:rounded-3xl bg-white border border-neutral-200/80 shadow-xs grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 text-left">
        {WORKFLOW_STEPS.map((step, i) => {
          const StepIcon = step.icon;
          return (
            <div key={i} className="p-3 sm:p-3.5 rounded-xl bg-neutral-50/60 border border-neutral-100 flex flex-col justify-between">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0 text-[#5B4DF5]">
                  <StepIcon className="w-3.5 h-3.5" />
                </div>
                <h5 className="text-xs sm:text-sm font-bold text-neutral-900 leading-tight">
                  {step.title}
                </h5>
              </div>
              <p className="text-[11px] text-neutral-500 leading-relaxed font-normal">
                {step.desc}
              </p>
            </div>
          );
        })}
      </div>

      {/* ── 4. PRIMARY ACTION BUTTON (Schedule Interactive Live Demo) ───────── */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={() => openConsultationModal("Schedule Interactive Live Demo - Real Time Experience")}
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#4F46E5] hover:bg-[#4338CA] text-white text-xs sm:text-sm font-semibold shadow-lg shadow-indigo-500/25 transition-all hover:scale-105 active:scale-95 cursor-pointer"
        >
          <span>Schedule Interactive Live Demo</span>
          <ArrowRight className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
};
